import { fetchAllPages } from '@core/api/pagination';
import { errorBox } from '@core/components/AlertBox';
import { environment } from '@core/environment';
import { GrpcRestFetchTransport, RpcRestError, useAuth0Interceptor } from '@deepup/api-utils';
import {
  commonApiV1 as commonApi,
  deviceManagementApiV1 as deviceManagementApi,
} from '@deepup/apis';
import { deviceManagementClientApiV1 as deviceManagementClientApi } from '@deepup/internal-apis';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

export const useGetProjectScanners = () => {
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);

  const [scanners, setScanners] = useState<deviceManagementApi.Scanner[]>([]);

  const authInterceptor = useAuth0Interceptor();
  const client = useMemo(() => {
    const transportRest = new GrpcRestFetchTransport({
      baseUrl: environment.unifiedApiUrl,
      fetchInit: {
        credentials: 'include',
      },
      interceptors: [authInterceptor],
    });

    return new deviceManagementClientApi.DeviceManagementServiceClient(transportRest);
  }, [authInterceptor]);

  useEffect(() => {
    const fetchScanners = async () => {
      if (!projectId) {
        return;
      }

      try {
        const { data: items } = await fetchAllPages({
          fetchFunction: ({ options }) => {
            return client.listScanners({
              sortBy: deviceManagementApi.ListScannersRequest_SortBy.UNSPECIFIED,
              sortOrder: commonApi.SortOrder.ASC,
              serialLike: '',
              organizationId: '',
              organizationLike: '',
              projectId: projectId,
              projectLike: '',
              recipientCompanyName: '',
              deliveryModeState: deviceManagementApi.DeliveryModeState.UNSPECIFIED,
              isRecallCandidate: commonApi.BoolFilter.UNSPECIFIED,
              isInRecall: commonApi.BoolFilter.UNSPECIFIED,
              canRecordForRegularProjects: commonApi.BoolFilter.UNSPECIFIED,
              canRecordForMaintenanceProjects: commonApi.BoolFilter.UNSPECIFIED,
              canRecordForDensificationProjects: commonApi.BoolFilter.UNSPECIFIED,
              options,
            } as Parameters<typeof client.listScanners>[0]).response;
          },
        });

        setScanners(items);
      } catch (e) {
        if (e instanceof RpcRestError) {
          errorBox(e.body?.message ?? 'Failed to fetch scanners');
        } else {
          errorBox('Failed to fetch scanners');
        }
      }
    };

    fetchScanners();
  }, [projectId]);

  return { scanners };
};
